import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
} from "react-bootstrap";
import SubscriptionService from "../../services/SubscriptionService";
import { API_STATUS } from "../../constants/apiStatusCode";
import EventService from "../../services/EventService";
import UserService from "../../services/UserService";
import PaymentService from "../../services/PaymentService";
import AccountService from "../../services/AccountService";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { loadRazorpay, cleanupRazorpay } from "../../utils/razorpay";
import UserSessionManager from "../../utils/UserSessionManager";

const ACCOUNT_PROVISION_SUCCESS_MESSAGE =
  "Account created successfully, you will need to sign-in again to complete the process";

const closePaymentVerificationModal = async () => {
  Swal.close();
  await new Promise((resolve) => setTimeout(resolve, 100));
};

const PAYMENT_VERIFICATION_COLOR = "rgb(204, 85, 0)";

const getPaymentVerificationModalHtml = () => `
  <div
        class="spinner-border"
        role="status"
        style="
          width: 2.25rem;
          height: 2.25rem;
          border-color: rgba(204, 85, 0, 0.28);
          border-right-color: ${PAYMENT_VERIFICATION_COLOR};
        "
      >
        <span class="visually-hidden">Loading...</span>
      </div>
`;

const LeadDashboard = () => {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [webinarsLoading, setWebinarsLoading] = useState(true);
  const [webinarsError, setWebinarsError] = useState(null);
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [domainModalOpen, setDomainModalOpen] = useState(false);
  const [domainNameInput, setDomainNameInput] = useState("");
  const [companyNameInput, setCompanyNameInput] = useState("");
  const [isCompanyNameFromProfile, setIsCompanyNameFromProfile] =
    useState(false);
  const [domainCheckLoading, setDomainCheckLoading] = useState(false);
  const [domainCheckError, setDomainCheckError] = useState("");
  const [isDomainAvailable, setIsDomainAvailable] = useState(false);
  const [accountCreateLoading, setAccountCreateLoading] = useState(false);
  const [accountCreateError, setAccountCreateError] = useState("");
  const [accountProvisionSuccess, setAccountProvisionSuccess] = useState(false);
  const [activationPlan, setActivationPlan] = useState(null);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    whatsAppNo: "",
    businessDetails: "",
    companyName: "",
    pricingPlanCode: "",
  });
  const [welcomeDisplayName, setWelcomeDisplayName] = useState('');
  const [enrolledPlanLabel, setEnrolledPlanLabel] = useState('');
  const [orderId, setOrderId] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      setPlansError(null);
      try {
        const resp = await SubscriptionService.getPricingPlan();
        const data = resp?.data ?? resp;
        const list = data?.data ?? [];
        const mapped = list
          .filter((p) => p.isActive !== false)
          .map((p) => ({
            title: p.title ?? "",
            term: p.termMonths,
            cost: p.cost,
            productCode: p.productCode,
            pricingPlanCode: p.pricingPlanCode,
            productId: p.productId,
            features: Array.isArray(p.features) ? p.features : [],
          }));
        setPricingPlans(mapped);
      } catch (e) {
        setPricingPlans([]);
        if (e.response?.data?.statusCode === API_STATUS.NOT_FOUND) {
          setPlansError(e.response.data.message);
        } else {
          setPlansError(
            "Unable to load pricing plans. Please try again later.",
          );
        }
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await UserService.fetchUserProfile();
        console.log("User_Response", response);

        if (response.data?.success && response.data.data) {
          const userDetails = response.data.data;
          const profileCompanyName = (userDetails?.companyName || "").trim();
          const fullName = [userDetails.firstName, userDetails.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();
          setWelcomeDisplayName(fullName);
          setEnrolledPlanLabel(userDetails.activationPlan);
          setUserData({
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            email: userDetails.email,
            whatsAppNo: userDetails.whatsAppNo,
            businessDetails: userDetails.businessDetails,
            companyName: profileCompanyName,
            pricingPlanCode: userDetails.pricingPlanCode,
          });
          setCompanyNameInput(profileCompanyName);
          setIsCompanyNameFromProfile(Boolean(profileCompanyName));
          fetchWebinars(userDetails.email);
        } else {
          console.error("API did not return expected data format");
        }
      } catch (error) {
        console.error("Error fetching profile details:", error);
      }
    })();
  }, []);

  useEffect(() => {
    fetchRazorPay();
  }, []);

  const fetchRazorPay = async () => {
    try {
      const resp = await EventService.getRazorPayKey();
      if (resp) {
        const data = resp;
        setRazorpayKeyId(data?.data?.data?.razorpayKeyId || "");
      }
    } catch (err) {
      console.log("No existing Razorpay profile");
    }
  };

  const fetchWebinars = async (email) => {
    setWebinarsLoading(true);
    setWebinarsError(null);
    try {
      const resp = await EventService.getUpcomingWebinar(email);
      const data = resp?.data ?? resp;
      const list = data?.data ?? [];
      const mapped = (Array.isArray(list) ? list : []).map((w) => ({
        id: w?.id,
        title: w?.title ?? "",
        date: w?.date ?? "",
        daysLeft: w?.daysLeft,
      }));
      setWebinars(mapped);
    } catch (e) {
      setWebinars([]);
      if (e.response?.data?.statusCode === API_STATUS.NOT_FOUND) {
        //setWebinarsError("No upcoming webinars found.");
      } else {
        setWebinarsError(
          "Unable to load upcoming webinars. Please try again later.",
        );
      }
    } finally {
      setWebinarsLoading(false);
    }
  };

  // Palette matched to provided screenshot
  const peachBanner = "#E9B48B";
  const bannerText = "#003B64";
  const bannerRightText = "#2D3748";
  const burntOrange = "#A0522D";
  const welcomeCardBg = "#FFF5EB";
  const pageBg = "#FFFFFF";
  const lightCardBg = "#F6E3D3";
  const brownTab = "#A7652E";
  const activateBtn = "#C17A4A";

  const formatInr = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString("en-IN");
  };

  const welcomeFeatures = [
    "Create campaigns using customizable templates",
    "Create your portal with drag-and-drop dashboard",
    "Schedule follow-ups via WhatsApp + email",
    "Track leads + analyze performance",
    "Use a professional dashboard tailored to you",
  ];

  const getTodayISO = () => new Date().toISOString();

  const addMonthsToTodayISO = (months) => {
    const d = new Date();
    const m = Number(months);
    d.setMonth(d.getMonth() + (Number.isFinite(m) ? m : 0));
    return d.toISOString();
  };

  const sanitizeCompanyName = (value) =>
    (value || "").replace(/[^a-zA-Z0-9]/g, "");

  const openDomainAvailabilityModal = (plan) => {
    setActivationPlan(plan);
    setDomainModalOpen(true);
    setDomainNameInput("");
    setDomainCheckLoading(false);
    setDomainCheckError("");
    setIsDomainAvailable(false);
    setAccountCreateLoading(false);
    setAccountCreateError("");
    setAccountProvisionSuccess(false);
  };

  const closeDomainModal = () => {
    setDomainModalOpen(false);
    setAccountProvisionSuccess(false);
  };

  const goToSignInAfterProvision = () => {
    closeDomainModal();
    navigate("/signin");
  };

  const handleCheckDomainAvailability = async () => {
    const domainName = (domainNameInput || "").trim();
    if (!domainName) {
      setDomainCheckError("Please enter a domain name.");
      return;
    }

    setDomainCheckLoading(true);
    setDomainCheckError("");
    setIsDomainAvailable(false);

    try {
      const resp = await AccountService.checkDomain({ domainName });
      const ok = !resp.data.data;
      if (!ok) {
        const msg =
          resp?.data?.message ||
          resp?.data?.data?.message ||
          "Domain is not available.";
        setDomainCheckError(msg);
        return;
      }
      setIsDomainAvailable(true);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to check domain availability. Please try again.";
      setDomainCheckError(msg);
    } finally {
      setDomainCheckLoading(false);
    }
  };

  const handleCreateAccountAndMigrate = async () => {
    const domainName = (domainNameInput || "").trim();
    const companyName = (companyNameInput || "").trim();
    const termMonths =
      Number(activationPlan?.termMonths ?? activationPlan?.term ?? 0) || 0;

    if (!companyName) {
      setAccountCreateError("Company name is required.");
      return;
    }
    if (!domainName) {
      setAccountCreateError("Domain name is required.");
      return;
    }
    if (!termMonths) {
      setAccountCreateError(
        "Invalid plan term; cannot calculate activate-to date.",
      );
      return;
    }

    setAccountCreateLoading(true);
    setAccountCreateError("");
    const sanitizedCompanyName = sanitizeCompanyName(companyName);
    localStorage.setItem("companyName", sanitizedCompanyName);

    try {
      const todayIso = getTodayISO();
      const activateToIso = addMonthsToTodayISO(termMonths);

      const createPayload = {
        accountName: companyName,
        accountDomainName: domainName,
        accountDatabaseName: sanitizedCompanyName,
        isMasterTenant: true,
        isActive: true,
        activeFrom: todayIso,
        activeTo: activateToIso,
        activePlan: activationPlan?.title || '',
        productCode: activationPlan?.productCode || '',
        pricingPlanCode: activationPlan?.pricingPlanCode || '',
        productId: activationPlan?.productId || '',
        orderId: orderId,
      };

      const createResp = await AccountService.createAccount(createPayload);
      const createOk = createResp.data.success;
      if (!createOk) {
        throw new Error(
          createResp?.data?.message || "Failed to create account.",
        );
      }

      const migratePayload = { dbname: sanitizedCompanyName };
      const migrateResp = await AccountService.migrateDatabase(migratePayload);
      const migrateOk = migrateResp.data;
      if (migrateOk) {
        setAccountProvisionSuccess(true);
      } else {
        throw new Error(
          migrateResp?.data?.message || "Failed to migrate database.",
        );
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === API_STATUS.CONFLICT) {
        setAccountProvisionSuccess(true);
      } else {
        var msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to create account and migrate database.";
        setAccountCreateError(msg);
      }
    } finally {
      setAccountCreateLoading(false);
    }
  };

  const handleSubmit = async (plan) => {
    const paymentRequest = mapFormValuesToPaymentRequest(plan);
    try {
      const res = await PaymentService.createPricingOrder(paymentRequest);
      if (res.data.success) {
        const order = res.data.data;
        setOrderId(order.razorpayOrderId);
        openRazorpayCheckout(order, plan);
      }
    } catch (err) {
      console.error("Payment order error:", err);
      alert(err.response?.data?.message || "Error initiating payment");
    }
  };

  const openRazorpayCheckout = async (order, plan) => {
    const loaded = await loadRazorpay();
    if (!loaded || !window.Razorpay) {
      const SWEET_ALERT_CONFIRM_COLOR = 'rgb(208 125 63)';
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to load Razorpay SDK. Please check your internet connection.',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
      return;
    }

    const options = {
      key: razorpayKeyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.razorpayOrderId,
      name: plan.title,
      description: "Pricing Plan Activation",

      prefill: {
        name: userData.firstName + " " + userData.lastName,
        email: userData.email,
        contact: userData.whatsAppNo,
      },
      handler: async function (response) {
        console.log("🔹 Razorpay Handler Response:", response);
        cleanupRazorpay();
        // 3️⃣ Step 3: Verify Payment via backend
        Swal.fire({
          title: "Verifying Payment",
          html: `
    <div style="text-align:center">
      <p>Please wait while we verify your payment.</p>
      ${getPaymentVerificationModalHtml()}
    </div>
  `,
          width: 460,
          padding: 0,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          customClass: {
            popup: "payment-popup",
            title: "payment-title",
            htmlContainer: "payment-html-container",
          },
        });

        let verifyRes;
        let verifyError;
        try {
          verifyRes = await PaymentService.verifyPricingPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });
        } catch (err) {
          verifyError = err;
        } finally {
          await closePaymentVerificationModal();
        }

        if (verifyError) {
          console.error(verifyError);
          const errorMsg = "Verification failed. Check logs.";
          alert(errorMsg);
          return;
        }

        try {
          const msg = verifyRes.data.data.isSuccess
            ? "Payment successful and registration completed!"
            : "Payment Verification Failed!";
          const isSuccess = verifyRes.data.data.isSuccess;
          alert(msg);
          if (isSuccess) {
            openDomainAvailabilityModal(plan);
          }
        } catch (err) {
          console.error(err);
          const errorMsg = "Verification failed. Check logs.";
          alert(errorMsg);
        }
      },

      theme: {
        color: "#3399cc",
      },
      modal: {
        ondismiss: function () {
          cleanupRazorpay();
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const mapFormValuesToPaymentRequest = (plan) => {
    return {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      whatsAppNumber: userData.whatsAppNo,
      companyName: userData.companyName,
      amount: parseFloat(plan.cost * plan.term || 0),
      currency: "INR",
      paymentMethod: "netbanking",
      eventId: null,
      source: "Product",
      productId: plan.productId,
    };
  };

  return (
    <div style={{ backgroundColor: pageBg, minHeight: "100%" }}>
      <style>{`
        .lead-dashboard-welcome-title { font-family: Georgia, 'Times New Roman', serif; }
        .lead-dashboard-tab-title { font-family: Georgia, 'Times New Roman', serif; }
        .lead-dashboard-plan-card {
          background: #F3DCCB;
          border-radius: 4px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
        }
        .lead-dashboard-plan-tab-wrap { position: relative; padding-left: 14px; height: 0; }
        .lead-dashboard-plan-tab {
          background: #B26B0E;
          color: #fff;
          font-family: Georgia, 'Times New Roman', serif;
          font-weight: 700;
          font-size: 15px;
          padding: 4px 12px;
          border-radius: 2px;
          display: inline-block;
          position: relative;
          top: -14px;
          box-shadow: 0 1px 0 rgba(0,0,0,0.2);
        }
        .lead-dashboard-plan-body {
          font-family: Georgia, 'Times New Roman', serif;
          color: #1f2937;
          font-size: 13px;
          line-height: 1.35;
        }
        .lead-dashboard-plan-subtitle { font-weight: 700; text-decoration: underline; }
        .lead-dashboard-activate-btn {
          background: #C9793D !important;
          border: 1px solid #7A4A20 !important;
          border-radius: 4px !important;
          height: 32px;
          width: 78%;
          margin: 0 auto;
          display: block;
          font-family: Georgia, 'Times New Roman', serif;
          font-weight: 700;
          font-size: 13px;
          padding: 0;
        }
      `}</style>

      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          backgroundColor: peachBanner,
          height: 64,
          padding: "0 48px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18, lineHeight: 1, color: bannerText }}>
            🎉
          </span>
          <span
            style={{
              color: bannerText,
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            Welcome {welcomeDisplayName}!
          </span>
        </div>
        <div
          style={{
            color: bannerRightText,
            fontSize: 14,
          }}
        >
          {enrolledPlanLabel
            ? `Enrolled in ${enrolledPlanLabel}`
            : "No active subscription plan"}
        </div>
      </div>

      <Container className="py-4">
        <Card
          className="mb-4 border-0 shadow-sm"
          style={{
            backgroundColor: welcomeCardBg,
            borderLeft: `4px solid ${burntOrange}`,
            borderRadius: "12px",
          }}
        >
          <Card.Body className="p-4">
            <h4
              className="lead-dashboard-welcome-title mb-3"
              style={{ color: burntOrange, fontWeight: 600 }}
            >
              🎉 Welcome to Nurturix!
            </h4>
            <p className="mb-3 text-secondary" style={{ fontSize: "0.95rem" }}>
              Here is your one-stop place to grow your business, at your pace.
            </p>
            <ul className="list-unstyled mb-0">
              {welcomeFeatures.map((text, i) => (
                <li key={i} className="d-flex align-items-start gap-2 mb-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 text-white"
                    style={{
                      width: 22,
                      height: 22,
                      backgroundColor: "#22c55e",
                      fontSize: 12,
                      lineHeight: 1,
                      marginTop: 2,
                    }}
                    aria-hidden
                  >
                    ✓
                  </span>
                  <span className="text-dark" style={{ fontSize: "0.95rem" }}>
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>

        <div
          className="mb-4 border bg-white rounded-2 overflow-hidden"
          style={{ borderColor: "#dee2e6" }}
        >
          <div
            className="lead-dashboard-tab-title text-white px-4 py-2 d-inline-block"
            style={{
              backgroundColor: brownTab,
              borderRadius: "0 0 8px 0",
              fontSize: "1.05rem",
            }}
          >
            Upcoming Webinars
          </div>
          <div className="p-4 pt-3">
            <p className="text-secondary small mb-4">
              Your upcoming webinars will show up here, if you are not seeing
              them here and want to join one, please{" "}
              <span
                onClick={() => navigate("/support")}
                className="text-primary text-decoration-underline"
                style={{ cursor: "pointer" }}
              >
                contact us
              </span>{" "}
              and we will add you to our next webinar.
            </p>

            {webinarsLoading && (
              <div className="text-center text-muted py-4">
                Loading upcoming webinars...
              </div>
            )}
            {webinarsError && !webinarsLoading && (
              <div className="text-center text-danger py-4 small">
                {webinarsError}
              </div>
            )}
            {!webinarsLoading && webinars.length === 0 && (
              <div className="text-center text-muted py-4">
                No upcoming webinars.
              </div>
            )}
            {!webinarsLoading &&
              !webinarsError &&
              webinars.map((webinar, idx) => {
                const days = webinar.daysLeft;
                const daysLabel =
                  typeof days === "number"
                    ? days === 0
                      ? "Today"
                      : `${days} more day${days === 1 ? "" : "s"} left`
                    : (days ?? "");

                return (
                  <div
                    key={webinar.id ?? `${webinar.title}-${idx}`}
                    className="border p-3 mb-2 bg-white d-flex flex-column flex-md-row align-items-md-center"
                    style={{ borderColor: "#dee2e6" }}
                  >
                    <div className="flex-grow-1">
                      <span className="text-muted me-2">{idx + 1}.</span>
                      <span className="fw-semibold text-dark">
                        {webinar.title || "-"}
                      </span>
                    </div>
                    <div
                      className="text-secondary small text-md-center"
                      style={{ width: 320, flex: "0 0 auto" }}
                    >
                      Date: {webinar.date || "-"}
                    </div>
                    <div
                      className="text-secondary small text-md-end"
                      style={{ width: 140, flex: "0 0 auto" }}
                    >
                      {daysLabel || "-"}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <h6
          className="fw-bold mb-5 lead-dashboard-tab-title"
          style={{ color: brownTab, fontSize: "1.1rem" }}
        >
          Pricing Plans
        </h6>
        <Row className="g-3">
          {plansLoading && (
            <Col className="text-center py-3 text-muted">
              Loading pricing plans...
            </Col>
          )}
          {plansError && (
            <Col className="text-center py-3 text-danger small">
              {plansError}
            </Col>
          )}
          {!plansLoading &&
            !plansError &&
            pricingPlans.map((plan, index) => (
              <Col md={4} key={index}>
                <Card
                  className="h-100 border lead-dashboard-plan-card"
                  style={{ borderColor: "#e8dfd4" }}
                >
                  <div className="lead-dashboard-plan-tab-wrap">
                    <span className="lead-dashboard-plan-tab">
                      {plan.title}
                    </span>
                  </div>

                  <Card.Body className="d-flex flex-column p-3 pt-4 lead-dashboard-plan-body">
                    {plan.term != null && (
                      <div className="mb-2">
                        Plan term ~ {plan.term} months.
                      </div>
                    )}
                    {plan.cost != null && plan.cost !== "" && (
                      <div className="mb-3">
                        Cost: {formatInr(plan.cost)} INR + Tax
                      </div>
                    )}

                    <div className="lead-dashboard-plan-subtitle mb-2">
                      What&apos;s included.
                    </div>

                    {plan.features.length > 0 && (
                      <div>
                        {plan.features.map((feature, i) => (
                          <div
                            key={i}
                            className="d-flex align-items-start gap-2 mb-2"
                          >
                            <span
                              style={{
                                color: "#111",
                                fontSize: 14,
                                lineHeight: 1.1,
                              }}
                            >
                              ✓
                            </span>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-auto pt-3 pb-1">
                      {!UserSessionManager.isDeveloper() && (
                      <Button
                        className="lead-dashboard-activate-btn text-white"
                        onClick={() => handleSubmit(plan)}
                      >
                        Activate
                      </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
        </Row>

        <Modal
          show={domainModalOpen}
          onHide={closeDomainModal}
          centered
          backdrop="static"
        >
          <Modal.Header
            closeButton
            style={{ backgroundColor: burntOrange, color: "#fff" }}
          >
            <Modal.Title>Check Domain Availability</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {accountProvisionSuccess ? (
              <>
                <p className="mb-3 text-dark" role="status">
                  {ACCOUNT_PROVISION_SUCCESS_MESSAGE}
                </p>
                <div className="d-flex justify-content-end">
                  <Button variant="primary" onClick={goToSignInAfterProvision}>
                    Continue to sign in
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Company name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={companyNameInput}
                    onChange={(e) => {
                      setCompanyNameInput(e.target.value);
                      setAccountCreateError("");
                    }}
                    placeholder="Enter company name"
                    disabled={
                      isCompanyNameFromProfile ||
                      domainCheckLoading ||
                      accountCreateLoading
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Domain name</Form.Label>
                  <Form.Control
                    value={domainNameInput}
                    onChange={(e) => {
                      setDomainNameInput(e.target.value);
                      setIsDomainAvailable(false);
                      setDomainCheckError("");
                      setAccountCreateError("");
                    }}
                    placeholder="example.com"
                    disabled={domainCheckLoading || accountCreateLoading}
                  />
                </Form.Group>

                <div className="d-flex gap-2 flex-wrap mb-2">
                  <Button
                    variant="outline-dark"
                    style={{ borderColor: burntOrange, color: burntOrange }}
                    onClick={handleCheckDomainAvailability}
                    disabled={
                      domainCheckLoading ||
                      accountCreateLoading ||
                      !(companyNameInput || "").trim() ||
                      !(domainNameInput || "").trim()
                    }
                  >
                    {domainCheckLoading ? "Checking..." : "Check availability"}
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handleCreateAccountAndMigrate}
                    disabled={!isDomainAvailable || accountCreateLoading}
                  >
                    {accountCreateLoading ? "Creating..." : "Create account"}
                  </Button>
                </div>

                {domainCheckError && (
                  <div className="text-danger small mb-2">
                    {domainCheckError}
                  </div>
                )}

                {isDomainAvailable && !domainCheckError && (
                  <div className="text-success small mb-2">
                    Domain is available. You can create your account.
                  </div>
                )}

                {accountCreateError && (
                  <div className="text-danger small mb-2">
                    {accountCreateError}
                  </div>
                )}
              </>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default LeadDashboard;
